import { useState, useEffect } from 'react';
import { Plus, Trash2, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import AdminLayout from '@/components/admin/AdminLayout';
import { supabase } from '@/integrations/supabase/client';

interface Author { id: string; name: string; bio: string | null; avatar_url: string | null; }

const AdminBlogAuthors = () => {
  const { toast } = useToast();
  const [authors, setAuthors] = useState<Author[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', bio: '', avatar_url: '' });

  const load = async () => {
    const { data } = await supabase.from('blog_authors').select('*').order('name');
    setAuthors(data || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name.trim()) return toast({ title: 'Name is required', variant: 'destructive' });
    const { error } = await supabase.from('blog_authors').insert({
      name: form.name.trim(), bio: form.bio.trim() || null, avatar_url: form.avatar_url.trim() || null,
    });
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else { setForm({ name: '', bio: '', avatar_url: '' }); toast({ title: 'Author added' }); load(); }
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from('blog_authors').delete().eq('id', id);
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else load();
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-4xl">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Blog Authors</h1>
          <p className="text-muted-foreground mt-1">Bylines available to the article generator</p>
        </div>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Plus className="h-4 w-4" />New author</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Input placeholder="Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              <Input placeholder="Avatar URL (optional)" value={form.avatar_url} onChange={e => setForm({ ...form, avatar_url: e.target.value })} />
            </div>
            <Textarea placeholder="Short bio (optional)" value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} />
            <Button onClick={add}><Save className="mr-2 h-4 w-4" />Save author</Button>
          </CardContent>
        </Card>

        <Card><CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Bio</TableHead><TableHead className="w-16" /></TableRow></TableHeader>
              <TableBody>
                {authors.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8">No authors yet</TableCell></TableRow>}
                {authors.map(a => (
                  <TableRow key={a.id}>
                    <TableCell className="font-medium">{a.name}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{a.bio}</TableCell>
                    <TableCell className="text-right"><Button variant="ghost" size="icon" onClick={() => remove(a.id)}><Trash2 className="h-4 w-4" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent></Card>
      </div>
    </AdminLayout>
  );
};

export default AdminBlogAuthors;
